import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppDataContext = createContext(null);

export const useAppData = () => {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used within AppDataProvider');
  return ctx;
};

// Hook for user-specific fo_data rows (fetches + provides CRUD)
export const useUserData = (userId, category) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!userId || !category) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/fo-data/${category}`);
      setData(res.data || []);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [userId, category]);

  useEffect(() => { fetch(); }, [fetch]);

  const insert = async (payload) => {
    const res = await axios.post(`${API}/fo-data/${category}`, payload);
    setData(prev => [res.data, ...prev]);
    return res.data;
  };

  const update = async (id, patch) => {
    const res = await axios.patch(`${API}/fo-data/${id}`, patch);
    setData(prev => prev.map(r => r.id === id ? res.data : r));
    return res.data;
  };

  const remove = async (id) => {
    await axios.delete(`${API}/fo-data/${id}`);
    setData(prev => prev.filter(r => r.id !== id));
  };

  return { data, loading, refetch: fetch, insert, update, remove };
};

// Convert rows array to { key: row } map
const toMap = (rows = []) => Object.fromEntries((rows || []).map(r => [r.key, r]));

export const AppDataProvider = ({ children }) => {
  const [config, setConfig] = useState({});
  const [configLoading, setConfigLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/fo-data/config`)
      .then(res => {
        setConfig(res.data || {});
      })
      .catch(() => setConfig({}))
      .finally(() => setConfigLoading(false));
  }, []);

  const value = {
    configLoading,
    // ── system config by category ──────────────────────────
    assetClasses:       config.asset_class      || [],
    assetClassMap:      toMap(config.asset_class),
    transactionTypes:   config.transaction_type  || [],
    transactionTypeMap: toMap(config.transaction_type),
    dealStages:         config.deal_stage        || [],
    dealStageMap:       toMap(config.deal_stage),
    approvalStatuses:   config.approval_status   || [],
    approvalStatusMap:  toMap(config.approval_status),
    approvalTypes:      config.approval_type     || [],
    approvalTypeMap:    toMap(config.approval_type),
    alertTypes:         config.alert_type        || [],
    alertTypeMap:       toMap(config.alert_type),
    reportTypes:        config.report_type       || [],
    reportFormats:      config.report_format     || [],
    reportStatuses:     config.report_status     || [],
    reportStatusMap:    toMap(config.report_status),
    propertyTypes:      config.property_type     || [],
    propertyStatuses:   config.property_status   || [],
    startupStages:      config.startup_stage     || [],
    startupSectors:     config.startup_sector    || [],
    insightTypes:       config.insight_type      || [],
    insightTypeMap:     toMap(config.insight_type),
    priorityLevels:     config.priority_level    || [],
    priorityLevelMap:   toMap(config.priority_level),
    financialYears:     config.financial_year    || [],
    brokers:            config.broker            || [],
    importSources:      config.import_source     || [],
    marketIndexes:      config.market_index      || [],
    categoryColors:     config.category_color    || [],
    categoryColorMap:   toMap(config.category_color),
    aiSuggestions:      config.ai_suggestion     || [],
  };

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
};
